import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, PALETTE, WARN, alpha, clamp, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Status } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * ENGINEER · RECONNECT THE WIRES — drag each loose wire to its socket.
 *
 * A wall panel with its wires pulled: coloured cables hang from a conduit on the left, each ending
 * in a round plug that wears a sign (circle, triangle, square, diamond, cross), and a junction box
 * on the right has a socket for each, in another order. Drag a plug to the socket of the same
 * colour and sign (or tap the plug, then the socket). The wrong socket is the mistake: the board
 * shakes, the socket rings red, and the plug springs back. A plug let go over nothing just springs
 * back. The status line counts the wires in: WIRES 1 / 3.
 *
 * Round 1 has three wires, round 2 three, round 3 four; the sockets keep their colours in every
 * round. The colours are the kit's six told-apart ones (`PALETTE`), and the signs are on every plug
 * and socket, so no round needs colour vision. (Sockets with only their signs, `socketColour:
 * false`, are still in the rules; no round deals them since 6 Oct 2026.)
 *
 * Converted to the kit (kit.tsx): the rules as plain functions, a small view state (`WiresView`),
 * a pure `View`, the `Board` that plays it on the game clock, and the `demo` the how-to plays.
 */

export type Sign = 'circle' | 'triangle' | 'square' | 'diamond' | 'cross';

export interface WireKind {
  colour: string;
  sign: Sign;
}

/** The five wires, each with its own colour (kit `PALETTE`) and its own sign. */
export const WIRE_KINDS: readonly WireKind[] = [
  { colour: PALETTE[0], sign: 'circle' },
  { colour: PALETTE[1], sign: 'triangle' },
  { colour: PALETTE[2], sign: 'square' },
  { colour: PALETTE[3], sign: 'diamond' },
  { colour: PALETTE[4], sign: 'cross' },
];

export interface WireLevel {
  count: number;
  /** Whether the sockets show their colour, or only their sign. */
  socketColour: boolean;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const WIRE_LEVELS: readonly WireLevel[] = easier(
  [
    { count: 3, socketColour: true },
    { count: 4, socketColour: true },
  ],
);

export interface WirePanel {
  /** The kind of wire hanging at each row on the left, top to bottom. */
  wires: number[];
  /** The kind of socket at each row on the right. */
  sockets: number[];
}

function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * One panel: `count` of the five kinds down the left in a random order, and their sockets down the
 * right in another, with no wire's socket straight across from it.
 */
export function dealPanel(level: WireLevel, rand: () => number): WirePanel {
  const kinds = shuffle(
    WIRE_KINDS.map((_, i) => i),
    rand,
  ).slice(0, level.count);
  const wires = shuffle(kinds, rand);
  let sockets = shuffle(kinds, rand);
  for (let tries = 0; tries < 200 && sockets.some((k, row) => wires[row] === k); tries++) sockets = shuffle(kinds, rand);
  return { wires, sockets };
}

export type PlugResult = 'ok' | 'wrong' | 'taken';

/** Plugging the wire at `wireRow` into the socket at `socketRow`: `filled` marks the sockets in use. */
export function plugInto(panel: WirePanel, wireRow: number, socketRow: number, filled: readonly boolean[]): PlugResult {
  if (filled[socketRow]) return 'taken';
  return panel.wires[wireRow] === panel.sockets[socketRow] ? 'ok' : 'wrong';
}

/** The nearest of `points` within `radius` of (x, y), or -1. */
export function nearest(points: readonly { x: number; y: number }[], x: number, y: number, radius: number): number {
  let best = -1;
  let bestD = radius;
  points.forEach((p, i) => {
    const d = Math.hypot(p.x - x, p.y - y);
    if (d <= bestD) {
      best = i;
      bestD = d;
    }
  });
  return best;
}

// -- the view -------------------------------------------------------------------------------------

/** Everything the panel shows. */
export interface WiresView {
  /** The game clock, ms. */
  t: number;
  /** The kind (WIRE_KINDS) of wire at each row on the left, and of socket at each row on the right. */
  w: readonly number[];
  k: readonly number[];
  /** The sockets show their colours (else only their signs). */
  sc: boolean;
  /** Where each wire's plug is, by row (units). */
  p: readonly (readonly [x: number, y: number])[];
  /** The socket each wire is plugged into, by row, or −1. */
  pl: readonly number[];
  /** The wire in hand (drawn on top), or −1. */
  g: number;
  /** The wire picked by a tap, waiting for a tap on its socket, or −1. */
  sel: number;
  /** A wrong socket, while it rings: which, and how long ago (ms). */
  bad: readonly [socket: number, age: number] | null;
  /** 0 playing, 1 all connected. */
  s: 0 | 1;
  fx?: Fx | undefined;
}

const W = BOARD.w;
const H = BOARD.h;
const ROOT_X = 28;
const REST_X = 80;
const SOCKET_X = 266;
const PLUG_R = 17;
/** A finger this near a plug picks it up; this near a socket drops it in. */
const GRAB_R = 32;
const DROP_R = 36;
const SPRING_MS = 240;
/** A wrong socket rings red this long. */
const WRONG_MS = 700;

export const rowY = (row: number, n: number) => q(24 + ((row + 0.5) * (H - 48)) / n, 1);

/** A wire's sign, centred on (x, y), about `s` across the middle. */
export function SignMark({ sign, x, y, s, fill }: { sign: Sign; x: number; y: number; s: number; fill: string }) {
  switch (sign) {
    case 'circle':
      return <circle cx={x} cy={y} r={s * 0.9} fill={fill} />;
    case 'triangle':
      return <path d={`M${x} ${y - s * 1.1} L${x + s * 1.1} ${y + s * 0.8} L${x - s * 1.1} ${y + s * 0.8} Z`} fill={fill} />;
    case 'square':
      return <rect x={x - s * 0.82} y={y - s * 0.82} width={s * 1.64} height={s * 1.64} rx={s * 0.15} fill={fill} />;
    case 'diamond':
      return <path d={`M${x} ${y - s * 1.2} L${x + s * 1.05} ${y} L${x} ${y + s * 1.2} L${x - s * 1.05} ${y} Z`} fill={fill} />;
    case 'cross':
      return (
        <path
          d={`M${x - s * 0.34} ${y - s * 1.05} h${s * 0.68} v${s * 0.71} h${s * 0.71} v${s * 0.68} h${-s * 0.71} v${s * 0.71} h${-s * 0.68} v${-s * 0.71} h${-s * 0.71} v${-s * 0.68} h${s * 0.71} Z`}
          fill={fill}
        />
      );
  }
}

/** A cable from the conduit to a plug's back, hanging a little. */
const cablePath = (x0: number, y0: number, x1: number, y1: number) => {
  const sag = 10 + Math.abs(y1 - y0) * 0.12 + Math.max(0, x1 - x0) * 0.05;
  const reach = Math.max(24, (x1 - x0) * 0.4);
  return `M${x0} ${y0} C${x0 + reach} ${y0 + sag} ${x1 - reach} ${y1 + sag} ${x1} ${y1}`;
};

/** The panel, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<WiresView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const n = view.w.length;
  // A demo glides every number between its keys: states and indices are read rounded.
  const plugged = view.pl.map((s) => Math.round(s));
  const inHand = Math.round(view.g);
  const sel = Math.round(view.sel);
  const done = Math.round(view.s) === 1;
  const bad = view.bad && view.bad[1] < WRONG_MS ? view.bad : null;
  const connected = plugged.filter((s) => s >= 0).length;
  const status: Status = done
    ? { text: say(lang, '✓ ALL CONNECTED', '✓ ALT KOBLET'), tone: 'ok' }
    : bad
      ? { text: say(lang, 'WRONG SOCKET — TRY ANOTHER', 'FEIL KONTAKT – PRØV EN ANNEN'), tone: 'bad' }
      : {
          text: say(lang, 'WIRES', 'LEDNINGER'),
          count: [connected, n],
          hint:
            sel >= 0
              ? say(lang, 'NOW TAP ITS SOCKET', 'TRYKK NÅ PÅ KONTAKTEN')
              : view.sc
                ? say(lang, 'DRAG PLUG TO SOCKET', 'DRA PLUGG TIL KONTAKT')
                : say(lang, 'MATCH THE SIGNS', 'FINN SAMME TEGN'),
        };
  // The wires to draw: plugged first, then loose, the one in hand last.
  const order = view.w
    .map((_, row) => row)
    .sort((a, b) => {
      const rank = (row: number) => (row === inHand ? 2 : plugged[row]! >= 0 ? 0 : 1);
      return rank(a) - rank(b);
    });
  const flow = view.t * 0.09;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}plate`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#26303a" stopOpacity=".55" />
          <stop offset="1" stopColor="#0b0f14" stopOpacity=".55" />
        </linearGradient>
        <linearGradient id={`${id}conduit`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#22292f" />
          <stop offset=".45" stopColor="#8f9ba6" />
          <stop offset="1" stopColor="#1c2228" />
        </linearGradient>
        <linearGradient id={`${id}box`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c353f" />
          <stop offset="1" stopColor="#141a20" />
        </linearGradient>
        <radialGradient id={`${id}bezel`} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor="#eef3f7" />
          <stop offset=".55" stopColor="#7c8894" />
          <stop offset="1" stopColor="#2b3238" />
        </radialGradient>
        <radialGradient id={`${id}shine`} cx=".35" cy=".3" r=".7">
          <stop offset="0" stopColor={INK.hi} stopOpacity=".55" />
          <stop offset=".5" stopColor={INK.hi} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* the wall panel, its rivets, and the conduit the wires come out of */}
      <rect x="6" y="6" width={W - 12} height={H - 12} rx="10" fill={`url(#${id}plate)`} stroke={skin.ink} strokeOpacity=".22" />
      {[
        [16, 16],
        [W - 16, 16],
        [16, H - 16],
        [W - 16, H - 16],
      ].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="2.4" fill="#8f9ba6" opacity=".55" />
      ))}
      <rect x="10" y="12" width="16" height={H - 24} rx="8" fill={`url(#${id}conduit)`} />
      {view.w.map((_, row) => (
        <rect key={row} x="20" y={rowY(row, n) - 7} width="10" height="14" rx="2" fill="#39424b" stroke="#9aa6b1" strokeOpacity=".5" />
      ))}

      {/* the junction box and its sockets */}
      <rect x="232" y="12" width="76" height={H - 24} rx="8" fill={`url(#${id}box)`} stroke={skin.ink} strokeOpacity=".3" />
      <circle cx="296" cy="24" r="4" fill={done ? OK : alpha(BAD, 0.3)} stroke="#000000" strokeOpacity=".5" />
      {view.k.map((kindIx, row) => {
        const kind = WIRE_KINDS[Math.round(kindIx)]!;
        const y = rowY(row, n);
        const ringing = bad !== null && Math.round(bad[0]) === row;
        const lit = plugged.includes(row);
        return (
          <g key={row}>
            <circle cx={SOCKET_X} cy={y} r="22" fill={`url(#${id}bezel)`} />
            <circle cx={SOCKET_X} cy={y} r="17.5" fill="none" stroke={ringing ? BAD : view.sc ? kind.colour : INK.dim} strokeWidth="4" opacity={ringing ? 1 : 0.9} />
            <circle cx={SOCKET_X} cy={y} r="14" fill="#05080b" />
            <SignMark sign={kind.sign} x={SOCKET_X} y={y} s={6.5} fill={view.sc ? kind.colour : INK.text} />
            {sel >= 0 && !lit && <circle cx={SOCKET_X} cy={y} r="26" fill="none" stroke={accent} strokeOpacity=".6" strokeWidth="1.5" strokeDasharray="4 4" />}
            <circle cx={SOCKET_X + 27} cy={y} r="3" fill={lit ? kind.colour : '#1a2026'} stroke="#000000" strokeOpacity=".5" />
            {/* the wrong socket sparks: a few short lines flying out, once */}
            {ringing &&
              [0, 1, 2, 3, 4, 5].map((s) => {
                const a = (s / 6) * Math.PI * 2 + 0.4;
                const f = bad[1] / WRONG_MS;
                const r0 = 24 + f * 16;
                return (
                  <line
                    key={s}
                    x1={SOCKET_X + Math.cos(a) * r0}
                    y1={y + Math.sin(a) * r0}
                    x2={SOCKET_X + Math.cos(a) * (r0 + 6)}
                    y2={y + Math.sin(a) * (r0 + 6)}
                    stroke={WARN}
                    strokeWidth="2"
                    strokeLinecap="round"
                    opacity={1 - f}
                  />
                );
              })}
          </g>
        );
      })}

      {/* the cables, then their plugs */}
      {order.map((row) => {
        const kind = WIRE_KINDS[Math.round(view.w[row]!)]!;
        const y0 = rowY(row, n);
        const [px, py] = view.p[row]!;
        const d = cablePath(ROOT_X, y0, px - PLUG_R + 2, py);
        const inSocket = plugged[row]! >= 0;
        return (
          <g key={row}>
            <path d={d} stroke="#000000" strokeOpacity=".45" strokeWidth="10" fill="none" strokeLinecap="round" transform="translate(1.5 3)" />
            <path d={d} stroke="#05070a" strokeWidth="9" fill="none" strokeLinecap="round" />
            <path d={d} stroke={kind.colour} strokeWidth="6.5" fill="none" strokeLinecap="round" />
            <path d={d} stroke={INK.hi} strokeOpacity=".35" strokeWidth="1.6" fill="none" strokeLinecap="round" transform="translate(0 -1.6)" />
            {done && <path d={d} stroke={INK.hi} strokeOpacity=".7" strokeWidth="2" fill="none" strokeDasharray="4 16" strokeDashoffset={-flow} />}
            {/* the plug: a sleeve where the cable joins, and the round head with its sign */}
            <rect x={px - PLUG_R - 8} y={py - 6} width="12" height="12" rx="3" fill="#8f9ba6" />
            <circle cx={px + 1.5} cy={py + 3} r={PLUG_R} fill="#000000" opacity={inSocket ? 0.2 : 0.4} />
            <circle cx={px} cy={py} r={PLUG_R} fill={kind.colour} stroke="#05070a" strokeWidth="2" />
            <circle cx={px} cy={py} r={PLUG_R} fill={`url(#${id}shine)`} />
            <SignMark sign={kind.sign} x={px} y={py} s={7} fill={INK.onFill} />
            {sel === row && <circle cx={px} cy={py} r={PLUG_R + 6} fill="none" stroke={accent} strokeWidth="2.5" />}
            {/* a loose end sparks a little */}
            {!inSocket &&
              row !== inHand &&
              [0, 1].map((i) => {
                const k = (((view.t / 1_400 + row * 0.37 + i * 0.5) % 1) + 1) % 1;
                return (
                  <circle
                    key={i}
                    cx={px + PLUG_R + 2 + k * 10}
                    cy={py - 4 + (i ? 1 : -1) * k * 8 + k * k * 12}
                    r={1.4 * (1 - k) + 0.4}
                    fill={WARN}
                    opacity={(1 - k) * 0.8}
                  />
                );
              })}
          </g>
        );
      })}
    </BoardFrame>
  );
}

// -- the board -----------------------------------------------------------------------------------

interface Drag {
  row: number;
  x: number;
  y: number;
  fromX: number;
  fromY: number;
  moved: boolean;
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = WIRE_LEVELS[level]!;
  const [panel] = useState(() => dealPanel(lv, seeded(Date.now() + level * 3_571)));
  const n = panel.wires.length;
  /** Which socket each wire is in, or -1. */
  const plugged = useRef<number[]>(panel.wires.map(() => -1));
  const drag = useRef<Drag | null>(null);
  const selected = useRef<number | null>(null);
  const returning = useRef<Map<number, { x: number; y: number; at: number }>>(new Map());
  const wrong = useRef<{ socket: number; at: number } | null>(null);

  const round = useRound(props, (_dt, now) => {
    for (const [row, r] of returning.current) if (now - r.at > SPRING_MS) returning.current.delete(row);
  });
  const now = round.now;

  const sockets = panel.sockets.map((_, row) => ({ x: SOCKET_X, y: rowY(row, n) }));
  const filled = () => sockets.map((_, s) => plugged.current.includes(s));
  const rest = (row: number) => ({ x: REST_X, y: rowY(row, n) });
  const plugAt = (row: number) => {
    const s = plugged.current[row]!;
    if (s >= 0) return sockets[s]!;
    const d = drag.current;
    if (d && d.row === row) return { x: d.x, y: d.y };
    const r = returning.current.get(row);
    const home = rest(row);
    if (r) {
      const k = Math.min(1, (now - r.at) / SPRING_MS);
      const e = 1 - (1 - k) * (1 - k);
      return { x: r.x + (home.x - r.x) * e, y: r.y + (home.y - r.y) * e };
    }
    return home;
  };

  const springBack = (row: number, from: { x: number; y: number }) => {
    returning.current.set(row, { x: from.x, y: from.y, at: round.time() });
  };

  const attempt = (row: number, socket: number, from: { x: number; y: number }) => {
    const r = plugInto(panel, row, socket, filled());
    selected.current = null;
    if (r === 'ok') {
      plugged.current[row] = socket;
      const done = plugged.current.filter((s) => s >= 0).length;
      round.hit();
      round.progress(done / n);
      if (done >= n) round.win();
      return;
    }
    springBack(row, from);
    if (r === 'wrong') {
      wrong.current = { socket, at: round.time() };
      round.miss();
    }
  };

  const down = (p: Pt) => {
    if (!round.live || drag.current) return;
    const loose = panel.wires.map((_, row) => row).filter((row) => plugged.current[row]! < 0);
    const pick = nearest(
      loose.map((row) => plugAt(row)),
      p.x,
      p.y,
      GRAB_R,
    );
    if (pick >= 0) {
      const row = loose[pick]!;
      returning.current.delete(row);
      const at = plugAt(row);
      drag.current = { row, x: at.x, y: at.y, fromX: p.x - at.x, fromY: p.y - at.y, moved: false };
      return;
    }
    // A plug picked by a tap, then a tap on a socket.
    if (selected.current !== null) {
      const s = nearest(sockets, p.x, p.y, DROP_R);
      if (s >= 0) attempt(selected.current, s, rest(selected.current));
      else selected.current = null;
    }
  };
  const move = (p: Pt) => {
    const d = drag.current;
    if (!d || !round.live) return;
    d.x = clamp(p.x - d.fromX, PLUG_R, W - PLUG_R);
    d.y = clamp(p.y - d.fromY, PLUG_R, H - PLUG_R);
    if (Math.hypot(d.x - rest(d.row).x, d.y - rest(d.row).y) > 10) d.moved = true;
  };
  const up = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    if (!round.live) {
      springBack(d.row, d);
      return;
    }
    if (!d.moved) {
      selected.current = selected.current === d.row ? null : d.row;
      return;
    }
    const s = nearest(sockets, d.x, d.y, DROP_R);
    if (s >= 0) attempt(d.row, s, d);
    else springBack(d.row, d);
  };

  const view: WiresView = {
    t: q(now),
    w: panel.wires,
    k: panel.sockets,
    sc: lv.socketColour,
    p: panel.wires.map((_, row) => {
      const at = plugAt(row);
      return [q(at.x), q(at.y)] as const;
    }),
    pl: [...plugged.current],
    g: drag.current?.row ?? -1,
    sel: selected.current ?? -1,
    bad: wrong.current && now - wrong.current.at < WRONG_MS ? [wrong.current.socket, q(now - wrong.current.at)] : null,
    s: round.won ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Wires and sockets: drag each plug to its socket', 'Ledninger og kontakter: dra hver plugg til kontakten'), down, move, up }}
    />
  );
}

// -- the how-to ----------------------------------------------------------------------------------

/** The how-to's panel: three wires, and where each one's socket is. */
const DEMO_WIRES = [1, 0, 2] as const;
const DEMO_SOCKETS = [0, 2, 1] as const;
/** The finger takes each plug in turn: when it picks it up, and when it is in. */
const DEMO_MOVES = [
  [900, 1_900],
  [2_600, 3_600],
  [4_300, 5_300],
] as const;

/**
 * The how-to: the finger takes each plug in turn and drags it across to the socket with its colour
 * and sign; the last one in, the panel is live.
 */
function demoWires(): DemoScript<WiresView> {
  const n = DEMO_WIRES.length;
  const socketOf = (row: number) => DEMO_SOCKETS.indexOf(DEMO_WIRES[row] as 0 | 1 | 2);
  const rest = (row: number): readonly [number, number] => [REST_X, rowY(row, n)];
  const at = (row: number): readonly [number, number] => [SOCKET_X, rowY(socketOf(row), n)];
  const keys: DemoKey<WiresView>[] = [
    {
      at: 0,
      view: { t: 0, w: [...DEMO_WIRES], k: [...DEMO_SOCKETS], sc: true, p: DEMO_WIRES.map((_, row) => rest(row)), pl: [-1, -1, -1], g: -1, sel: -1, bad: null, s: 0, fx: [0, 0] },
    },
  ];
  const finger: FingerKey[] = [
    { at: 0, x: 250, y: 300, hide: true },
    { at: 300, x: 250, y: 300, hide: false },
  ];
  const placed: number[] = [-1, -1, -1];
  const where = DEMO_WIRES.map((_, row) => rest(row));
  DEMO_MOVES.forEach(([pick, drop], row) => {
    where[row] = at(row);
    placed[row] = socketOf(row);
    keys.push(
      { at: pick, view: { g: row }, ease: 'step' },
      { at: drop, view: { p: [...where] }, ease: 'inOut' },
      { at: drop + 10, view: { pl: [...placed], g: -1, fx: [1, 0], ...(row === n - 1 ? { s: 1 as const } : {}) }, ease: 'step' },
      { at: drop + 450, view: { fx: [0, 0] } },
    );
    const [rx, ry] = rest(row);
    const [sx, sy] = at(row);
    finger.push(
      { at: pick - 400, x: rx, y: ry },
      { at: pick, x: rx, y: ry, down: true },
      { at: drop, x: sx, y: sy },
      { at: drop + 100, x: sx, y: sy, down: false },
    );
  });
  finger.push({ at: 6_200, x: 250, y: 300, hide: true });
  return {
    ms: 7_400,
    keys,
    finger,
    steps: [
      { at: 0, en: 'PUT A FINGER ON A PLUG', no: 'LEGG FINGEREN PÅ EN PLUGG' },
      { at: 1_000, en: 'DRAG IT TO ITS SOCKET', no: 'DRA DEN TIL KONTAKTEN' },
      { at: 2_300, en: 'SAME COLOUR, SAME SIGN', no: 'SAMME FARGE, SAMME TEGN' },
    ],
  };
}

export const game = thirtyGame<WiresView>({
  Board,
  View,
  demo: demoWires(),
  words: {
    en: {
      instruction: 'Drag each plug to the socket with the same colour and sign. The wrong one sparks.',
      howMove: 'Put your finger on a plug and drag it to its socket. Or tap the plug, then the socket.',
    },
    no: {
      instruction: 'Dra hver plugg til kontakten med samme farge og tegn. Feil kontakt gnistrer.',
      howMove: 'Legg fingeren på en plugg og dra den til kontakten. Eller trykk på pluggen, så kontakten.',
    },
  },
});
